import { useState, useEffect, Fragment } from "react";
import RGL, { WidthProvider } from "react-grid-layout";
import "./widgets-layout.scss";
import { FaPencilAlt } from "react-icons/fa";
import { PiDotsSixVerticalLight } from "react-icons/pi";
import { TiDelete } from "react-icons/ti";

import { IObject } from "../../../shared/utils/utils";
import { IWidgetLayout } from "./widget-component/widget-component";
import { Widget } from "./widget-utils";
import DateWidget from "./widgets/date-widget/date-widget";
import HeaderWidget from "./widgets/header-widget/header-widget";
import ListWidget from "./widgets/list-widget/list-widget";
import NumberWidget from "./widgets/number-widget/number-widget";
import ReferenceWidget from "./widgets/reference-widget/reference-widget";
import RelatedAlertedEventWidget from "./widgets/related-alerted-event-widget/related-alerted-event-widget";
import RelatedEntityWidget from "./widgets/related-entity-widget/related-entity-widget";
import RelatedEventWidget from "./widgets/related-event-widget/related-event-widget";
import TableWidget from "./widgets/table-widget/table-widget";
import FieldWidget from "./widgets/text-field-widget/text-field-widget";
import TextWidget from "./widgets/text-widget/text-widget";
import BooleanWidget from "./widgets/boolean-widget/boolean-widget";

export interface IWidgetLayoutConfig {
  x: number,
  y: number,
  w: number,
  h: number,
  i: string,
  minH?: number,
  minW?: number,
  maxH?: number,
  maxW?: number
}

export interface IWidgetLayoutProps {
  layout: IWidgetLayout;
  readonly?: boolean;
  dataReadonly?: boolean;
  data?: IObject;
  onLayoutChanged(layout: IWidgetLayoutConfig[]): void;
  onWidgetEdit(widget: Widget): void;
  onDataChanged?(newValue: string, key?: string): void;
}

const ReactGridLayout = WidthProvider(RGL);

const WidgetsLayout = ({layout, readonly = false, dataReadonly = false, data, onLayoutChanged, onWidgetEdit, onDataChanged}: IWidgetLayoutProps) => {
  const [showControls, setShowControls] = useState("");

  useEffect(() => {
    const event = document.createEvent("HTMLEvents");
    event.initEvent("resize", true, false);
    document.dispatchEvent(event);
  }, [layout]);

  const deleteBlock = (id: string) => {
    onLayoutChanged(layout.layout.filter(l => l.i !== id));
  };

  const renderWidgetComponent = (widget: Widget) => {
    switch (widget.type) {
      case "text": 
        return <TextWidget text={widget.text}/>;
      case "header":
        return <HeaderWidget text={widget.text}/>;
      case "field":
        widget.readonly = dataReadonly;
        if (data?.id && widget.primaryKey) {
          return <div className="widgetLayout-fieldValue">
            <p className="widgetLayout-label">{widget.label}</p>
            <p className="widgetLayout-value">{data?.[widget.id] || " "}&nbsp;</p>
          </div>;
        }
        return <FieldWidget value={data?.[widget.id]} widget={widget} onChange={onDataChanged} />;
      case "reference":
        widget.readonly = dataReadonly;
        return <ReferenceWidget previewMode={readonly === false} 
        selectedIds={
          data?.[widget.id]
          ? [{ id: data?.sygno_id_1, value: data?.[widget.id] }].map(item => item.id)
          : data?.sygno_id_1 ? [data?.sygno_id_1] : []
            }
        widget={widget} onChange={(newValue: string[]) => onDataChanged?.(newValue[0], widget.id)}/>;
      case "list":
        widget.readonly = dataReadonly;
        return <ListWidget widget={widget} value={data?.[widget.id]} onChange={onDataChanged}/>;
      case "date":
        widget.readonly = dataReadonly;
        return <DateWidget value={data?.[widget.id]} widget={widget} onChange={onDataChanged}/>;
      case "table":
        return <TableWidget title={widget.label} previewMode={readonly === false} columns={widget.columns} dataUrl={widget.dataUrl}/>;
      case "relatedEntity":
        return <RelatedEntityWidget widget={widget} previewMode={readonly === false}/>;
      case "relatedEvent":
        return <RelatedEventWidget widget={widget} previewMode={readonly === false}  alertcaseRowId={data?.sygno_id || data?.alertcaseRowId} relatedEntityTypeId={data?.relatedEntityTypeId} relatedEntityId={data?.relatedEntityId} alertcaseId={data?.alertcaseId}/>
      case "relatedAlertedEvent":
        return <RelatedAlertedEventWidget widget={widget} previewMode={readonly === false} alertcaseRowId={data?.sygno_id} alertcaseId={data?.alertcaseId}/>;
      case "integer":
      case "float":
        widget.readonly = dataReadonly;
        if (data?.id && widget.primaryKey){
          return <div className="widgetLayout-fieldValue">
            <p className="widgetLayout-label">{widget.label}</p>
            <p className="widgetLayout-value">{data?.[widget.id] || " "}&nbsp;</p>
          </div>;
        }
        return <NumberWidget value={data?.[widget.id]} widget={widget} onChange={onDataChanged}/>;
        case "boolean":
          widget.readonly = dataReadonly;
          return <BooleanWidget value={data?.[widget.id]} widget={widget} onChange={onDataChanged}/>;
      }
  };

  const renderWidget = (widget: Widget) => {
    return (
      <div onMouseLeave={() => setShowControls("")} onMouseOver={() => setShowControls(widget.id)} className="widgetLayout-blockWrapperMain" key={widget.id}>
        <div className={`widgetLayout-blockWrapper ${readonly && "widgetLayout-readonly"}`} key={widget.id}>
          {(!readonly && widget.id === showControls) && (
            <Fragment>
              <span className="widgetLayout-draggableBtn drag-handle">
                <PiDotsSixVerticalLight/>
              </span>
              <div className="widgetLayout-wrapperBlockBtns">
                <div className="widgetLayout-editBlockBtn" onClick={() => onWidgetEdit(widget)}>
                  <FaPencilAlt /> 
                </div>
                <div className="widgetLayout-deleteBlockBtn" onClick={() => deleteBlock(widget.id)}>
                  <TiDelete />
                </div>
              </div>
            </Fragment>
          )}
          {renderWidgetComponent(widget)}
        </div>
      </div>
    );
  };

  return (
    <div className="widgetLayout">
      <ReactGridLayout
        className="widgetLayout-gridLayout"
        draggableHandle={".drag-handle"}
        rowHeight={40}
        isDroppable={!readonly}
        layout={layout.layout}
        isResizable={!readonly}
        isDraggable={!readonly}
        onDragStop={onLayoutChanged}
        onResizeStop={onLayoutChanged}
      >
        {layout.layoutItems.map(renderWidget)}
      </ReactGridLayout>
    </div>
  );
};

export default WidgetsLayout;
